import * as React from 'react';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Paper from '@mui/material/Paper';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import SkeletonTableRows from './SkeletonTableRows';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import EditIcon from '@mui/icons-material/Edit';
import LogoutIcon from '@mui/icons-material/Logout';
import { visuallyHidden } from '@mui/utils';
import { useTranslation } from 'react-i18next';
import { useGetClubMembersQuery, useRemoveUserMutation, useUpdateMemberRoleMutation } from '../api/apiSlice';
import { ClubMember } from '../types/api';
import { Order, getComparator, stableSort } from '../utils/tableSort';
import EnhancedTableToolbar from './EnhancedTableToolbar';
import { useDecodedToken } from '../app/hooks';

const headCellsBase: { id: keyof ClubMember; numeric: boolean; disablePadding: boolean; labelKey: string; hideOnMobile?: boolean }[] = [
  {
    id: 'NAME',
    numeric: false,
    disablePadding: true,
    labelKey: 'userManagement.name',
  },
  {
    id: 'FIRSTNAME',
    numeric: false,
    disablePadding: false,
    labelKey: 'userManagement.firstName',
  },
  {
    id: 'PHONE',
    numeric: false,
    disablePadding: false,
    labelKey: 'userManagement.phone',
    hideOnMobile: true,
  },
  {
    id: 'EMAIL',
    numeric: false,
    disablePadding: false,
    labelKey: 'userManagement.email',
  },
  {
    id: 'ROLE',
    numeric: false,
    disablePadding: false,
    labelKey: 'userManagement.role',
  }
];

const hiddenOnMobileSx = { display: { xs: 'none', sm: 'table-cell' } } as const;

function formatPhoneNumber(phoneNumber: string | null) {
  if (!phoneNumber) return phoneNumber;
  const cleanedNumber = phoneNumber.replace(/\D/g, ''); // Remove non-numeric characters
  const formattedNumber = cleanedNumber.replace(/(\d{2})(\d{1})(\d{2})(\d{2})(\d{2})(\d{2})/, '+$1 $2 $3 $4 $5 $6');
  return formattedNumber;
}

interface EnhancedTableHeadProps {
  numSelected: number;
  order: Order;
  orderBy: string;
  rowCount: number;
  onRequestSort: (event: React.MouseEvent<unknown>, property: keyof ClubMember) => void;
  onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

function EnhancedTableHead(props: EnhancedTableHeadProps) {
  const { t } = useTranslation();
  const { onSelectAllClick, order, orderBy, numSelected, rowCount, onRequestSort } =
    props;
  const createSortHandler = (property: keyof ClubMember) => (event: React.MouseEvent<unknown>) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        <TableCell padding="checkbox">
          <Checkbox
            color="primary"
            indeterminate={numSelected > 0 && numSelected < rowCount}
            checked={rowCount > 0 && numSelected === rowCount}
            onChange={onSelectAllClick}
            inputProps={{
              'aria-label': t('userManagement.selectAll'),
            }}
          />
        </TableCell>
        {headCellsBase.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? 'right' : 'left'}
            padding={headCell.disablePadding ? 'none' : 'normal'}
            sortDirection={orderBy === headCell.id ? order : false}
            sx={headCell.hideOnMobile ? hiddenOnMobileSx : undefined}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
            >
              {t(headCell.labelKey as 'userManagement.name')}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === 'desc' ? t('common.sortedDescending') : t('common.sortedAscending')}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

interface UserManagementProps {
  clubId?: number;
}

export default function UserManagement({ clubId }: UserManagementProps) {
  const { t } = useTranslation();
  const [order, setOrder] = React.useState<Order>('asc');
  const [orderBy, setOrderBy] = React.useState<keyof ClubMember>('NAME');
  const [selected, setSelected] = React.useState<number[]>([]);
  const [errorSnackbarOpen, setErrorSnackbarOpen] = React.useState(false);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);
  const clubID = clubId != null ? String(clubId) : '';

  const { data: users = [], isLoading } = useGetClubMembersQuery(clubID, { skip: !clubID });
  const [removeUser] = useRemoveUserMutation();
  const [updateMemberRole] = useUpdateMemberRoleMutation();
  const ownId = useDecodedToken()?.id;

  const handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof ClubMember) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = users.map((user) => user.ID);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event: React.MouseEvent<unknown>, id: number) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected: number[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }
    setSelected(newSelected);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const isSelected = (id: number) => selected.indexOf(id) !== -1;

  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - users.length) : 0;

  const visibleRows = React.useMemo(
    () =>
      stableSort(users, getComparator<ClubMember, keyof ClubMember>(order, orderBy)).slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage,
      ),
    [order, orderBy, page, rowsPerPage, users],
  );

  const handleRemovetUser = async (user_id: number) => {
    try {
      await removeUser(user_id).unwrap();
    } catch (error) {
      console.error('Error:', error);
      setErrorSnackbarOpen(true);
    }
  }

  const handleDeleteSelected = async () => {
    const idsToRemove = selected.filter((id) => id !== ownId);
    try {
      await Promise.all(idsToRemove.map((id) => removeUser(id).unwrap()));
      setSelected([]);
    } catch (error) {
      console.error('Error:', error);
      setErrorSnackbarOpen(true);
    }
  }

  const handleEditUser = async (user_id: number) => {
    console.log("edit user");
  }

  const handleRoleChange = async (user_id: number, event: SelectChangeEvent) => {
    const role = event.target.value as 'USER' | 'ANIMATOR' | 'ADMIN';
    try {
      await updateMemberRole({ userId: user_id, role }).unwrap();
    } catch (error) {
      console.error('Error:', error);
      setErrorSnackbarOpen(true);
    }
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Paper sx={{ width: '100%', mb: 2 }}>
        <EnhancedTableToolbar numSelected={selected.length} count={users.length} onDeleteSelected={handleDeleteSelected} />
        <TableContainer sx={{ overflowX: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
          <Table
            sx={{ minWidth: { xs: 500, sm: 750 } }}
            aria-labelledby="tableTitle"
            size={'medium'}
          >
            <EnhancedTableHead
              numSelected={selected.length}
              order={order}
              orderBy={orderBy}
              onSelectAllClick={handleSelectAllClick}
              onRequestSort={handleRequestSort}
              rowCount={users.length}
            />
            <TableBody>
              {isLoading && (
                <SkeletonTableRows
                  rows={3}
                  cells={[
                    { variant: 'rectangular', width: 18, height: 18, cellProps: { padding: 'checkbox' } },
                    {},
                    {},
                    { cellProps: { sx: hiddenOnMobileSx } },
                    {},
                    {},
                    { variant: 'circular', width: 24, height: 24 },
                    { variant: 'circular', width: 24, height: 24 },
                  ]}
                />
              )}

              {!isLoading && users.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    {t('userManagement.empty')}
                  </TableCell>
                </TableRow>
              )}

              {visibleRows.map((row, index) => {
              const isItemSelected = isSelected(row.ID);
              const labelId = `enhanced-table-checkbox-${index}`;

              return (
                <TableRow
                  key={row.ID}
                  hover
                  role="checkbox"
                  aria-checked={isItemSelected}
                  tabIndex={-1}
                  selected={isItemSelected}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      color="primary"
                      checked={isItemSelected}
                      inputProps={{
                        'aria-labelledby': labelId,
                      }}

                      onClick={(event) => handleClick(event, row.ID)}
                    />
                  </TableCell>
                  <TableCell component="th" id={labelId} scope="row" padding="none">
                    {row.NAME}
                  </TableCell>
                  <TableCell align="right">{row.FIRSTNAME}</TableCell>
                  <TableCell align="right" sx={hiddenOnMobileSx}>{formatPhoneNumber(row.PHONE)}</TableCell>
                  <TableCell align="right">{row.EMAIL}</TableCell>
                  <TableCell align="right">
                    {row.ROLE === 'USER' || row.ROLE === 'ANIMATOR' ? (
                      <Select
                        size="small"
                        value={row.ROLE}
                        onChange={(event) => handleRoleChange(row.ID, event)}
                      >
                        <MenuItem value="USER">{t('userManagement.roles.user')}</MenuItem>
                        <MenuItem value="ANIMATOR">{t('userManagement.roles.animator')}</MenuItem>
                        <MenuItem value="ADMIN">{t('userManagement.roles.admin')}</MenuItem>
                      </Select>
                    ) : (
                      row.ROLE === 'ADMIN' ? t('userManagement.roles.admin') : row.ROLE
                    )}
                  </TableCell>

                  <TableCell align='right' component="th" scope="row">
                    <IconButton onClick={() => handleEditUser(row.ID)} aria-label={`${t('common.edit')}: ${row.NAME}`}>
                      <EditIcon color='primary'/>
                    </IconButton>
                  </TableCell>

                  <TableCell align='right' component="th" scope="row">
                    <IconButton
                      onClick={() => handleRemovetUser(row.ID)}
                      disabled={row.ID === ownId}
                      title={row.ID === ownId ? t('userManagement.cannotRemoveSelf') : undefined}
                      aria-label={row.ID === ownId ? t('userManagement.cannotRemoveSelf') : `${t('common.delete')}: ${row.NAME}`}
                    >
                      <LogoutIcon color={row.ID === ownId ? 'disabled' : 'error'} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
              {emptyRows > 0 && (
                <TableRow
                  style={{
                    height: (53) * emptyRows,
                  }}
                >
                  <TableCell colSpan={8} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={users.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
      <Snackbar open={errorSnackbarOpen} autoHideDuration={4000} onClose={() => setErrorSnackbarOpen(false)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={() => setErrorSnackbarOpen(false)} severity="error" variant="filled">
          {t('common.genericError')}
        </Alert>
      </Snackbar>
    </Box>
  );
}
